/**
 * Resizes an uploaded image to a PNG data URL suitable for sending to an AI model.
 *
 * @remarks
 * - This runs in the browser and uses Canvas. It's intentionally dependency-free.
 * - PNG size is controlled by downscaling dimensions (PNG doesn't have a reliable "quality" knob).
 * - Callers should treat the resulting data URL as sensitive (screenshots may contain PII).
 *
 * @see docs/features/BUG_TRACKER.md
 */
export interface ResizeImageToPngResult {
  dataUrl: string;
  byteSize: number;
  width: number;
  height: number;
}

export interface ResizeImageToPngOptions {
  maxDimensionPx?: number;
  minDimensionPx?: number;
  maxPngBytes?: number;
  maxInputBytes?: number;
}

export const DEFAULT_RESIZE_IMAGE_TO_PNG_OPTIONS: Required<ResizeImageToPngOptions> =
  {
    maxInputBytes: 10 * 1024 * 1024,
    maxDimensionPx: 1280,
    minDimensionPx: 320,
    maxPngBytes: 1_500_000,
  };

export const resolveResizeImageToPngOptions = (
  options?: ResizeImageToPngOptions,
): Required<ResizeImageToPngOptions> => {
  return {
    maxInputBytes:
      options?.maxInputBytes ??
      DEFAULT_RESIZE_IMAGE_TO_PNG_OPTIONS.maxInputBytes,
    maxDimensionPx:
      options?.maxDimensionPx ??
      DEFAULT_RESIZE_IMAGE_TO_PNG_OPTIONS.maxDimensionPx,
    minDimensionPx:
      options?.minDimensionPx ??
      DEFAULT_RESIZE_IMAGE_TO_PNG_OPTIONS.minDimensionPx,
    maxPngBytes:
      options?.maxPngBytes ?? DEFAULT_RESIZE_IMAGE_TO_PNG_OPTIONS.maxPngBytes,
  };
};

const blobToDataUrl = async (blob: Blob): Promise<string> => {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read image"));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string" || !result.startsWith("data:")) {
        reject(new Error("Invalid image data URL"));
        return;
      }
      resolve(result);
    };
    reader.readAsDataURL(blob);
  });
};

const canvasToPngBlob = async (canvas: HTMLCanvasElement): Promise<Blob> => {
  return await new Promise((resolve, reject) => {
    if (typeof canvas.toBlob !== "function") {
      reject(new Error("Canvas toBlob is not available"));
      return;
    }

    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Failed to encode PNG"));
        return;
      }
      resolve(blob);
    }, "image/png");
  });
};

const clampInt = (value: number, min: number): number => {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.floor(value));
};

export const computeInitialResizeScale = (
  originalMax: number,
  maxDimensionPx: number,
): number => {
  const scale = Math.min(1, maxDimensionPx / originalMax);
  if (!Number.isFinite(scale) || scale <= 0) {
    return 1;
  }
  return scale;
};

export const isResizeAtMinimumSize = (
  width: number,
  height: number,
  minDimensionPx: number,
): boolean => {
  return (
    Math.max(width, height) <= minDimensionPx || Math.min(width, height) <= 1
  );
};

export const validateImageFileForResize = (
  file: File,
  maxInputBytes: number,
): string => {
  if (!(file instanceof File)) {
    throw new Error("No image selected");
  }

  if (file.size <= 0) {
    throw new Error("Empty image file");
  }

  if (file.size > maxInputBytes) {
    throw new Error("Image file is too large");
  }

  const type = file.type.toLowerCase();
  if (!type.startsWith("image/")) {
    throw new Error("Unsupported file type (expected an image)");
  }

  return type;
};

const loadImageForCanvas = async (
  file: File,
): Promise<{
  width: number;
  height: number;
  draw: (ctx: CanvasRenderingContext2D, width: number, height: number) => void;
  cleanup: () => void;
}> => {
  // Prefer ImageBitmap for speed and reliable sizing.
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file);
    return {
      width: bitmap.width,
      height: bitmap.height,
      draw: (ctx, width, height) => {
        ctx.drawImage(bitmap, 0, 0, width, height);
      },
      cleanup: () => {
        try {
          bitmap.close();
        } catch {
          // ignore
        }
      },
    };
  }

  const objectUrl = URL.createObjectURL(file);

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to load image"));
    image.src = objectUrl;
  });

  return {
    width: img.naturalWidth,
    height: img.naturalHeight,
    draw: (ctx, width, height) => {
      ctx.drawImage(img, 0, 0, width, height);
    },
    cleanup: () => {
      try {
        URL.revokeObjectURL(objectUrl);
      } catch {
        // ignore
      }
    },
  };
};

export const resizeImageFileToPngDataUrl = async (
  file: File,
  options?: ResizeImageToPngOptions,
): Promise<ResizeImageToPngResult> => {
  const resolvedOptions = resolveResizeImageToPngOptions(options);
  const { maxInputBytes, maxDimensionPx, minDimensionPx, maxPngBytes } =
    resolvedOptions;

  validateImageFileForResize(file, maxInputBytes);

  const loaded = await loadImageForCanvas(file);
  try {
    const originalWidth = clampInt(loaded.width, 1);
    const originalHeight = clampInt(loaded.height, 1);
    const originalMax = Math.max(originalWidth, originalHeight);

    // Never upscale.
    let scale = computeInitialResizeScale(originalMax, maxDimensionPx);

    // We'll downscale further if the PNG ends up too large.
    const MAX_ATTEMPTS = 8;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
      const width = clampInt(originalWidth * scale, 1);
      const height = clampInt(originalHeight * scale, 1);

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        throw new Error("Canvas 2D context unavailable");
      }

      // Higher quality downscaling in browsers that support it.
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      loaded.draw(ctx, width, height);

      const blob = await canvasToPngBlob(canvas);

      // Accept if under byte threshold, or if we are already at minimum size.
      const atMinSize = isResizeAtMinimumSize(width, height, minDimensionPx);
      if (blob.size <= maxPngBytes || atMinSize) {
        const dataUrl = await blobToDataUrl(blob);
        return {
          dataUrl,
          byteSize: blob.size,
          width,
          height,
        };
      }

      // Downscale further to reduce PNG size; PNG doesn't support quality tuning.
      scale *= 0.85;
    }

    throw new Error("Failed to resize image to a reasonable size");
  } finally {
    loaded.cleanup();
  }
};
